<template>
  <div class="join-form">
    <div class="join-form--left">
      <div class="join-form--left--benefits">
        <div class="join-form--left--top">
          <h2>Ready to join?</h2>
          <p>
            Join with tens of thousands of other doctors and medical students
            across Australia. Through the collective power of our members, we
            deliver steadfast representation and unwavering support when you
            need it.
          </p>
        </div>
        <div class="join-form--left--slider">
          <swiper
            v-if="slides.length > 1"
            @swiper="onSwiper"
            @slideChange="onSlideChange"
            :slides-per-view="'auto'"
            :slides-per-group="1"
            :space-between="30"
            :centered-slides="true"
            :loop="false"
            :pagination="false"
          >
            <swiper-slide v-for="(slide, i) in slides" :key="`slides-${i}`">
              <div class="slide--image">
                <img :src="slide.image" />
              </div>
              <div class="slide--title">“{{ slide.content }}”</div>
              <div class="slide--person">{{ slide.name }}</div>
              <div class="slide--date">Member since {{ slide.year }}</div>
            </swiper-slide>
          </swiper>
          <div v-else class="justify-center d-flex">
            <div
              v-for="(slide, i) in slides"
              :key="`slides-${i}`"
              class="mx-auto swiper-slide"
            >
              <div class="slide--image">
                <img :src="slide.image" />
              </div>
              <div class="slide--title">“{{ slide.content }}”</div>
              <div class="slide--person">{{ slide.name }}</div>
              <div class="slide--date">Member since {{ slide.year }}</div>
            </div>
          </div>
          <div v-if="slides.length > 1" class="slide--pager">
            <a
              href="#"
              v-for="(slide, i) in slides"
              :key="`pager-${i}`"
              @click.prevent="moveSlider(i)"
              :class="{ active: slideIndex == i }"
            ></a>
          </div>
        </div>
      </div>
    </div>
    <div class="join-form--right">
      <div class="join-form--step step-seven">
        <div class="step-legend">
          <h2>Complete</h2>
          <step-circles step="5" />
        </div>
        <price-box
          :form="form"
          :options="options"
          :discount="discount"
          :prices="prices"
          :cpd="form.cpd_sub"
          formType="join"
        />
        <form @submit.prevent="next" class="nice-form">
          <div class="payment-selector">
            <div
              class="payment-selector--button"
              @click.prevent="$set(data, 'paymentType', 'cc')"
              :class="{ active: data.paymentType === 'cc' }"
            >
              <i class="fas fa-credit-card"></i>
              <span>Credit Card</span>
            </div>
            <div
              class="payment-selector--button year"
              @click.prevent="$set(data, 'paymentType', 'etf')"
              :class="{ active: data.paymentType === 'etf' }"
            >
              <i class="fal fa-university"></i>
              <span>Direct Debit</span>
            </div>
          </div>
          <div v-if="data.paymentType == 'cc' && !error" class="step-form">
            <custom-field
              label="Name on card"
              :rules="{
                required: { message: 'This field is required.' },
              }"
              v-model="data.card.name"
            />
            <custom-field
              label="Card number"
              :mask="cardMask"
              :rules="{
                required: { message: 'This field is required.' },
                cardNumber: {},
              }"
              v-model="data.card.number"
            >
              <template v-slot:suffix>
                <span v-if="cardLogo" class="field-suffix">
                  <svg viewBox="0 0 750 471">
                    <Amex v-if="cardLogo == 'american express'" />
                    <Diners v-if="cardLogo == 'diners'" />
                    <Discover v-if="cardLogo == 'discover'" />
                    <Jcb v-if="cardLogo == 'jcb'" />
                    <Maestro v-if="cardLogo == 'maestro'" />
                    <Mastercard v-if="cardLogo == 'mastercard'" />
                    <Visa v-if="cardLogo == 'visa'" />
                    <Unipay v-if="cardLogo == 'unionpay'" />
                  </svg>
                </span>
              </template>
            </custom-field>
            <div class="row">
              <div class="col-md-6">
                <custom-field
                  label="Expiry Date"
                  mask="##/##"
                  :rules="{
                    required: { message: 'This field is required.' },
                    cardExpiry: {},
                  }"
                  v-model="data.card.expiry"
                />
              </div>
              <div class="col-md-6">
                <custom-field
                  label="Security code - CCV"
                  :rules="{
                    required: { message: 'This field is required.' },
                    cardCCV: {},
                  }"
                  v-model="data.card.ccv"
                />
              </div>
            </div>
          </div>
          <div v-if="data.paymentType == 'etf' && !error" class="step-form">
            <div v-if="data.state == 'SA'">
              <custom-field
                label="Account name"
                :rules="{
                  required: { message: 'This field is required.' },
                }"
                v-model="data.etf.name"
              />
              <div class="row">
                <div class="col-md-6">
                  <custom-field
                    label="BSB Number"
                    :rules="{
                      required: { message: 'This field is required.' },
                    }"
                    v-model="data.etf.bsb"
                  />
                </div>
                <div class="col-md-6">
                  <custom-field
                    label="Account Number"
                    :rules="{
                      required: { message: 'This field is required.' },
                    }"
                    v-model="data.etf.number"
                  />
                </div>
              </div>
            </div>
            <div v-else class="etf-content">
              <p>Please call AMA NSW if you would like to pay using Direct Debit</p>
              <p><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="currentColor" d="M19.95 21q-3.125 0-6.175-1.362t-5.55-3.863q-2.5-2.5-3.862-5.55T3 4.05q0-.45.3-.75t.75-.3H8.1q.35 0 .625.238t.325.562l.65 3.5q.05.4-.025.675T9.4 8.45L6.975 10.9q.5.925 1.187 1.787t1.513 1.663q.775.775 1.625 1.438T13.1 17l2.35-2.35q.225-.225.588-.337t.712-.063l3.45.7q.35.1.575.363T21 15.9v4.05q0 .45-.3.75t-.75.3"/></svg> (02) 9439 8822</p>
            </div>
          </div>
          <div v-if="error" class="step-form--error">
            <template v-if="error == 'error'">
              <h6 class="error-title">Something happened!</h6>
              <p>
                Something went wrong, Please contact support
                <span v-if="imisID">and provide this number {{ imisID }}</span>
              </p>
            </template>
            <template v-else>
              <h6 class="error-title">Transaction Declined</h6>
              <p v-if="data.paymentType == 'cc'">
                The credit card details were declined. Please check the details
                and try again.
              </p>
              <p v-if="data.paymentType == 'etf'">
                The direct debit details were declined. Please check the details
                and try again.
              </p>
              <div class="error-buttons">
                <a href="#" @click.prevent="$emit('clear')">Try again </a>
              </div>
            </template>
          </div>
          <h6 v-if="data.paymentType == 'cc' && !error && data.state == 'SA' || data.paymentType == 'cc' && !error && data.state == 'NSW' || data.paymentType == 'etf' && !error && data.state == 'SA'">Declaration</h6>
          <div v-if="data.paymentType == 'cc' && !error && data.state == 'SA' || data.paymentType == 'cc' && !error && data.state == 'NSW' || data.paymentType == 'etf' && !error && data.state == 'SA'" class="form-checkbox">
            <input
              id="terms"
              class="d-none"
              type="checkbox"
              value="1"
              v-model="terms"
              required="true"
            />
            <label for="terms">
              <span v-if="terms">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
                  <path
                    fill="currentColor"
                    d="M400 480H48c-26.51 0-48-21.49-48-48V80c0-26.51 21.49-48 48-48h352c26.51 0 48 21.49 48 48v352c0 26.51-21.49 48-48 48zm-204.686-98.059l184-184c6.248-6.248 6.248-16.379 0-22.627l-22.627-22.627c-6.248-6.248-16.379-6.249-22.628 0L184 302.745l-70.059-70.059c-6.248-6.248-16.379-6.248-22.628 0l-22.627 22.627c-6.248 6.248-6.248 16.379 0 22.627l104 104c6.249 6.25 16.379 6.25 22.628.001z"
                  ></path>
                </svg>
              </span>
              <span v-else>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
                  <path
                    fill="currentColor"
                    d="M400 32H48C21.5 32 0 53.5 0 80v352c0 26.5 21.5 48 48 48h352c26.5 0 48-21.5 48-48V80c0-26.5-21.5-48-48-48zm-6 400H54c-3.3 0-6-2.7-6-6V86c0-3.3 2.7-6 6-6h340c3.3 0 6 2.7 6 6v340c0 3.3-2.7 6-6 6z"
                  ></path>
                </svg>
              </span>
              <span
                >I hereby agree to the
                <a href="/privacy-policy" target="_blank"
                  >Terms and Conditions</a
                >
                and apply to be an elected member of the Australian Medical
                Association Limited and agree, if elected, to abide by the
                <a
                  href="/position-statement/code-ethics-2004-editorially-revised-2006-revised-2016"
                  target="_blank"
                  >AMA Code of Ethics</a
                >
                and the Rules of the AMA.</span
              >
            </label>
          </div>
          <div v-if="loading" class="step-footer">
            <button
              type="button"
              class="button button-outline-blue button-nm"
              disabled="disabled"
            >
              Back
            </button>
            <button
              type="button"
              class="button button-blue button-nm"
              disabled="disabled"
            >
              <!---<i class="fas fa-spinner fa-spin"></i>-->
              <span class="loader"></span>
            </button>
          </div>
          <!---<div v-if="data.paymentType == 'etf' && !error && data.state == 'NSW'">
            <button
              type="button"
              @click.prevent="back"
              class="button button-outline-blue button-nm"
            >
              Back
            </button>
          </div>-->
          <div v-else class="step-footer">
            <button
              type="button"
              @click.prevent="back"
              class="button button-outline-blue button-nm"
            >
              Back
            </button>
            <!---<button
              v-if="form.cpd_sub"
              type="submit"
              class="button button-blue button-nm"
              id="cpd-button"
            >
              Pay ${{ prices[data.payment]["price"] | cpdTotal(form.cpd_sub) }}
              <br /><span>(Membership + CPD subscription)</span>
            </button>-->
            <button
              v-if="prices[data.payment] && data.state == 'NSW'"
              type="submit"
              class="button button-blue button-nm"
            >
              Submit
            </button>
            <button
              v-else-if="prices[data.payment]"
              type="submit"
              class="button button-blue button-nm"
            >
              Pay {{ prices[data.payment]["price"] | currency(discount) }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>
<script>
import { Navigation, Pagination } from "swiper";
import { SwiperCore, Swiper, SwiperSlide } from "swiper-vue2";
import stepCircles from "../stepCircles";
import PriceBox from "./PriceBox";
import customField from "../forms/customField";
import {
  Amex,
  Diners,
  Discover,
  Jcb,
  Maestro,
  Mastercard,
  Visa,
  Unipay,
} from "../icons";
SwiperCore.use([Navigation, Pagination]);
const cardMasks = [
  {
    mask: "#### ###### #####",
    regex: /^3[47]\d{0,13}/,
    cardtype: "american express",
  },
  {
    mask: "#### #### #### ####",
    regex: /^(?:6011|65\d{0,2}|64[4-9]\d?)\d{0,12}/,
    cardtype: "discover",
  },
  {
    mask: "#### ###### ####",
    regex: /^3(?:0([0-5]|9)|[689]\d?)\d{0,11}/,
    cardtype: "diners",
  },
  {
    mask: "#### #### #### ####",
    regex: /^(5[1-5]\d{0,2}|22[2-9]\d{0,1}|2[3-7]\d{0,2})\d{0,12}/,
    cardtype: "mastercard",
  },
  {
    mask: "#### ###### #####",
    regex: /^(?:2131|1800)\d{0,11}/,
    cardtype: "jcb15",
  },
  {
    mask: "#### #### #### ####",
    regex: /^(?:35\d{0,2})\d{0,12}/,
    cardtype: "jcb",
  },
  {
    mask: "#### #### #### ####",
    regex: /^(?:5[0678]\d{0,2}|6304|67\d{0,2})\d{0,12}/,
    cardtype: "maestro",
  },
  {
    mask: "#### #### #### ####",
    regex: /^4\d{0,15}/,
    cardtype: "visa",
  },
  {
    mask: "#### #### #### ####",
    regex: /^62\d{0,14}/,
    cardtype: "unionpay",
  },
];
export default {
  name: "step-six",
  props: ["step", "form", "loading", "error", "prices", "discount", "options"],
  components: {
    Swiper,
    SwiperSlide,
    stepCircles,
    Amex,
    Diners,
    Discover,
    Jcb,
    Maestro,
    Mastercard,
    Visa,
    Unipay,
    PriceBox,
    customField,
  },
  data() {
    return {
      imisID: null,
      cardLogo: false,
      terms: true,
      cardMask: "#### #### #### ####",
      slides: [],
      data: {
        card: {
          name: "",
          expiry: "",
          number: "",
          ccv: "",
        },
        etf: {
          name: "",
          bsb: "",
          number: "",
        },
      },
      slider: null,
      slideIndex: 0,
    };
  },
  watch: {
    error() {
      this.imisID = window.localStorage.getItem("imisid") || null;
    },
    "data.card.number"() {
      const value = this.data.card.number;
      if (value) {
        var currentCard = cardMasks.find((card) => value.match(card.regex));
        if (currentCard) {
          this.cardMask = currentCard.mask;
          this.cardLogo = currentCard.cardtype;
          this.data.card.type = currentCard.cardtype;
          return;
        }
      }

      this.cardLogo = false;
    },
  },
  filters: {
    cpdTotal(value, check) {
      if (check) {
        var total = (value + 220).toFixed(2);
        return total;
      }
    },
    currency(value, discount) {
      var vdiscount = 1 - parseFloat(discount).toFixed(2);
      return `$${(parseFloat(value) * vdiscount).toFixed(2)}`;
    },
  },
  methods: {
    onSwiper(swiper) {
      this.slider = swiper;
    },
    moveSlider(pos) {
      this.slider.slideTo(pos);
    },
    onSlideChange() {
      this.slideIndex = this.slider.activeIndex;
    },
    back() {
      this.$emit("clear");
      this.$emit("update:step", 5);
    },
    next() {
      let failed = false;
      for (var i in this.$children) {
        const child = this.$children[i];
        if (child.onFocus) {
          const errors = child.onBlur();
          if (errors.length) {
            failed = true;
          }
        }
      }

      if (failed) return;
      this.$emit("update:form", this.data);
      this.$emit("process");
    },
  },
  mounted() {
    this.data = this.form;
    if (this.data.state == "NSW") {
      this.slides = [
        {
          image: "/modules/amacustom/ama/ama_dashboard/images/devin-deo.png",
          content:
            "With AMA (NSW), it has consistently provided a means to give a voice to DIT like me. It has resulted in positive change for the wellbeing of DITs.",
          name: "Dr Devin Deo",
          year: "2020",
        },
      ];
    } else {
      this.slides = [
        {
          image:
            "/modules/amacustom/ama/ama_dashboard/images/emerson-krstic.png",
          content:
            "The importance of an advocacy group for doctors, and being a part of making working conditions better for junior doctors",
          name: "Dr Emerson Krstic",
          year: "2022",
        },
        {
          image: "/modules/amacustom/ama/ama_dashboard/images/hayden-cain.png",
          content:
            "I want my voice to be heard and help implement change to benefit patients, the public and health practitioners",
          name: "Dr Hayden Cain",
          year: "2019",
        },
      ];
    }
  },
};
</script>

<style scoped>
#cpd-button span {
  color: #fff;
  font-size: 0.8rem;
}
</style>
